/**
 * Helper compartido de formateo y parseo de números (web + mobile).
 *
 * - Locale-aware: lee el locale del dispositivo (navigator.language en web,
 *   Intl en mobile/Hermes) con fallback 'es-AR'. En es-AR → "1.234,56".
 * - Funciones PURAS: sin importar window/Platform → safe en web y Hermes.
 * - No importa getUserLocale() de dateHelper (ese usa navigator solo o Platform
 *   de RN, que rompen el otro runtime). Tiene su propio detector agnóstico.
 *
 * Ver docs/2026-07-20-formato-numeros-design.md
 */

import { toSafeLocale } from './safeLocale';

type NullableNumber = number | null | undefined;

interface FormatOptions {
  decimals?: number;
}

interface WeightOptions {
  decimals?: number;
  unit?: string;
}

/**
 * Detector de locale agnóstico de plataforma.
 * - Web: navigator.language.
 * - Mobile/Hermes: Intl.NumberFormat().resolvedOptions().locale.
 * - Fallback: 'es-AR' (también si el tag del dispositivo es inválido para Intl).
 */
function getLocale(): string {
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return toSafeLocale(navigator.language);
    }
  } catch {
    /* navigator inaccesible (Hermes) */
  }
  try {
    return toSafeLocale(Intl.NumberFormat().resolvedOptions().locale);
  } catch {
    return 'es-AR';
  }
}

/**
 * Formatea un número con separadores locales.
 * @param value número (null/undefined → "0")
 * @param opts.decimals cantidad de decimales (default 0, redondeo half-up)
 */
export function formatNumber(value: NullableNumber, opts: FormatOptions = {}): string {
  const decimals = opts.decimals ?? 0;
  const n = value ?? 0;
  return new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    roundingMode: 'halfExpand',
  }).format(Number(n) || 0);
}

/**
 * Formatea moneda: prefijo "$" + 0 decimales (siempre, redondea half-up).
 * El número interno queda preciso; solo el display redondea.
 */
export function formatMoney(value: NullableNumber): string {
  return `$${formatNumber(value, { decimals: 0 })}`;
}

/**
 * Formatea monto según la moneda: USD con 2 decimales y prefijo "US$";
 * ARS / constante (ARS de hoy) con enteros y "$" (igual que formatMoney).
 * Pensado para zonas con toggle ARS/USD/constante (ej. costos históricos KAN-171).
 */
export function formatMoneyMoneda(value: NullableNumber, moneda?: string): string {
  if (moneda === 'USD') return `US$ ${formatNumber(value, { decimals: 2 })}`;
  return formatMoney(value);
}

/**
 * Formatea peso con sufijo de unidad.
 * @param value número (null/undefined → "--")
 * @param opts.decimals default 0
 * @param opts.unit default "kg" (pasar '' para sin sufijo)
 */
export function formatWeight(value: NullableNumber, opts: WeightOptions = {}): string {
  const decimals = opts.decimals ?? 0;
  const unit = opts.unit ?? 'kg';
  if (value === null || value === undefined) return '--';
  const formatted = formatNumber(value, { decimals });
  return unit ? `${formatted} ${unit}` : formatted;
}

/**
 * Parseo robusto de un string numérico con coma/punto a number.
 * Regla: el ÚLTIMO separador presente es el decimal.
 *   - "1.234,56" → 1234.56  (AR: . miles, , decimal)
 *   - "400,5"    → 400.5    (coma decimal suelta, mobile)
 *   - "400.5"    → 400.5    (canonical / web type=number)
 *   - "1,234.56" → 1234.56  (US: , miles, . decimal)
 *   - "1.234"    → 1.234    (punto-solo = decimal, input directo)
 * Corrige el parseFloat de mobile (que truncaba "400,5" → 400) y el
 * replace(',', '.') del CSV (que solo reemplazaba la primera coma).
 */
export function parseNumber(input: string | number | null | undefined): number {
  if (typeof input === 'number') return input;
  if (input === null || input === undefined) return 0;
  let s = String(input).trim();
  if (!s) return 0;

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  const commaCount = (s.match(/,/g) || []).length;
  const dotCount = (s.match(/\./g) || []).length;

  if (lastComma > -1 && lastDot > -1) {
    // Hay ambos separadores: el ÚLTIMO es el decimal, el otro es miles
    if (lastComma > lastDot) {
      // AR: punto = miles, coma = decimal
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // US: coma = miles, punto = decimal
      s = s.replace(/,/g, '');
    }
  } else if (commaCount >= 2) {
    // Múltiples comas sin punto → miles (US: "1,250,000")
    s = s.replace(/,/g, '');
  } else if (dotCount >= 2) {
    // Múltiples puntos sin coma → miles (AR: "1.250.000")
    s = s.replace(/\./g, '');
  } else if (lastComma > -1) {
    // Una sola coma → decimal (input AR: "400,5")
    s = s.replace(',', '.');
  }
  // Una sola coma/punto o ninguno → parseFloat directo

  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}
